import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMail, FiUser, FiGift, FiAward, FiInfo } from 'react-icons/fi';
import { supabase, getWeightedRandomPrize, MOCK_PRIZES } from './lib/supabase';

import ScratchCard from './components/games/ScratchCard';
import Roulette from './components/games/Roulette';
import SlotMachine from './components/games/SlotMachine';

const GAME_TYPES = {
  scratch: { id: 'scratch', label: 'スクラッチ', icon: '🎫' },
  roulette: { id: 'roulette', label: 'ルーレット', icon: '🎡' },
  slot: { id: 'slot', label: 'スロット', icon: '🎰' }
};

export default function App() {
  const [step, setStep] = useState(1); // 1: Email, 2: Select/Play, 3: Result
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  
  // User Data
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [userData, setUserData] = useState(null);

  // Game Data
  const [gameType, setGameType] = useState('scratch');
  const [availablePrizes, setAvailablePrizes] = useState([]);
  const [selectedPrize, setSelectedPrize] = useState(null);
  const [playResult, setPlayResult] = useState(null); // Data from RPC

  // Parse URL params on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gameParam = params.get('game');
    if (gameParam && GAME_TYPES[gameParam]) {
      setGameType(gameParam);
    }
  }, []);

  // Handle Email Submit
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setErrorMsg('');

    try {
      // 1. Check if user exists
      const { data: members, error } = await supabase
        .from('remo_members')
        .select('*')
        .eq('email', email)
        .limit(1);

      if (error && error.code !== 'PGRST116') {
        // Suppress error if table doesn't exist for demo purposes
        console.warn("DB Error, falling back to mock mode", error);
      }

      if (members && members.length > 0) {
        // Existing user
        setUserData(members[0]);
        setName(members[0].name || '');
        await prepareGame(members[0].name);
      } else {
        // New user
        setIsNewUser(true);
        // Wait for name input
      }
    } catch (err) {
      console.error(err);
      // Fallback for demo
      await prepareGame('ゲスト');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Name Submit for New Users
  const handleNameSubmit = async (e) => {
    e.preventDefault();
    if (!name) return;

    setIsLoading(true);
    try {
      // Insert new user
      const { error } = await supabase
        .from('remo_members')
        .insert([{ email, name }]);
        
      if (error) console.warn("Insert failed, might be mock mode", error);
      
      setUserData({ email, name });
      await prepareGame(name);
    } catch (err) {
      console.error(err);
      await prepareGame(name);
    } finally {
      setIsLoading(false);
    }
  };

  // Prepare game data before showing game screen
  const prepareGame = async (userName) => {
    setIsLoading(true);
    try {
      // Fetch config for selected game type
      const { data: configData, error } = await supabase
        .from('remo_gacha_config')
        .select('*')
        .eq('active', true)
        .eq('gacha_type', gameType)
        .limit(1);

      let items = MOCK_PRIZES; // Default fallback

      if (configData && configData.length > 0 && configData[0].items) {
        items = configData[0].items;
      }

      setAvailablePrizes(items);
      
      // Select prize upfront
      const prize = getWeightedRandomPrize(items);
      setSelectedPrize(prize);

      setStep(2); // Move to Game step
    } catch (err) {
      console.error("Failed to prepare game", err);
      // Fallback
      setAvailablePrizes(MOCK_PRIZES);
      setSelectedPrize(getWeightedRandomPrize(MOCK_PRIZES));
      setStep(2);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Game Completion
  const handleGameComplete = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const sessionParam = params.get('session') || 'lunch';

      // Call RPC to record score
      const { data, error } = await supabase.rpc('remo_record_action', {
        p_email: email,
        p_name: name,
        p_action_type: 'gacha_play',
        p_points: selectedPrize.points,
        p_session: sessionParam,
        p_detail: {
          gacha_type: gameType,
          result: selectedPrize.label
        }
      });

      if (error) {
        console.warn("RPC Error, using mock result", error);
        // Mock successful result if RPC fails (e.g., table not setup)
        setPlayResult({
          success: true,
          total_points: (userData?.total_points || 0) + selectedPrize.points,
          rank: Math.floor(Math.random() * 50) + 1
        });
      } else {
        setPlayResult(data);
      }
      
      setStep(3); // Move to Result step
    } catch (err) {
      console.error(err);
      // Fallback mock result
      setPlayResult({
        success: true,
        total_points: 500 + selectedPrize.points,
        rank: 12
      });
      setStep(3);
    } finally {
      setIsLoading(false);
    }
  };

  // Render Helpers
  const renderHeader = () => (
    <div className="text-center mb-6">
      <h1 className="text-2xl font-black text-white flex items-center justify-center gap-2 drop-shadow-md">
        <span className="text-3xl">🎰</span> 
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-remo-main to-green-300">
          デジコラボ
        </span>
        デイリーガチャ
      </h1>
    </div>
  );

  return (
    <div className="min-h-[100dvh] w-full bg-gradient-to-br from-remo-dark via-remo-sub to-remo-dark flex items-center justify-center p-4 font-sans relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-[-10%] left-[-10%] w-64 h-64 bg-remo-main rounded-full mix-blend-screen filter blur-[100px] opacity-20 animate-pulse"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-64 h-64 bg-remo-accent rounded-full mix-blend-screen filter blur-[100px] opacity-10 animate-pulse" style={{ animationDelay: '1s' }}></div>

      <div className="w-full max-w-md relative z-10">
        {renderHeader()}

        <AnimatePresence mode="wait">
          {/* STEP 1: LOGIN / INFO */}
          {step === 1 && (
            <motion.div 
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="glass-card rounded-2xl p-6 shadow-2xl"
            >
              {!isNewUser ? (
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                      <FiMail /> メールアドレス
                    </label>
                    <input 
                      type="email" 
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="例: user@example.com"
                      className="w-full bg-black/30 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-remo-main focus:ring-1 focus:ring-remo-main transition-colors"
                    />
                  </div>
                  <button 
                    type="submit" 
                    disabled={isLoading || !email}
                    className="w-full bg-remo-main hover:bg-green-600 text-white font-bold py-3 px-4 rounded-lg transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? '確認中...' : 'スタート'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleNameSubmit} className="space-y-4">
                  <div className="text-center mb-4 text-remo-accent">
                    <FiInfo className="inline mb-1 mr-1" />
                    初めてのご利用ですね！お名前を教えてください。
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                      <FiUser /> お名前（ニックネーム可）
                    </label>
                    <input 
                      type="text" 
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="例: デジコ太郎"
                      className="w-full bg-black/30 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-remo-main focus:ring-1 focus:ring-remo-main transition-colors"
                    />
                  </div>
                  <button 
                    type="submit" 
                    disabled={isLoading || !name}
                    className="w-full bg-remo-main hover:bg-green-600 text-white font-bold py-3 px-4 rounded-lg transition-colors shadow-lg disabled:opacity-50"
                  >
                    {isLoading ? '準備中...' : 'ガチャを引く'}
                  </button>
                </form>
              )}
            </motion.div>
          )}

          {/* STEP 2: GAMEPLAY */}
          {step === 2 && (
            <motion.div 
              key="step2"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.1 }}
              className="glass-card rounded-2xl p-6 shadow-2xl flex flex-col items-center"
            >
              <div className="w-full text-center mb-6">
                <p className="text-gray-300 mb-2">
                  <span className="font-bold text-white">{name}</span> さん、おかえりなさい！
                </p>
                
                {/* Game Tabs (Optional, if URL param isn't strictly enforced) */}
                <div className="flex justify-center gap-2 mt-4 bg-black/20 p-1 rounded-lg">
                  {Object.values(GAME_TYPES).map(type => (
                    <button
                      key={type.id}
                      onClick={() => setGameType(type.id)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${gameType === type.id ? 'bg-remo-main text-white shadow-md' : 'text-gray-400 hover:text-white'}`}
                    >
                      {type.icon} {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="w-full py-4">
                {gameType === 'scratch' && <ScratchCard prize={selectedPrize} onComplete={handleGameComplete} />}
                {gameType === 'roulette' && <Roulette prize={selectedPrize} allPrizes={availablePrizes} onComplete={handleGameComplete} />}
                {gameType === 'slot' && <SlotMachine prize={selectedPrize} onComplete={handleGameComplete} />}
              </div>
            </motion.div>
          )}

          {/* STEP 3: RESULT */}
          {step === 3 && (
            <motion.div 
              key="step3"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card rounded-2xl p-8 shadow-2xl text-center relative overflow-hidden"
            >
              {/* Confetti / Glitter Effect Background */}
              {playResult?.success !== false && (
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-50">
                  <div className="w-full h-full bg-[radial-gradient(circle,rgba(255,215,0,0.2)_0%,transparent_70%)] animate-pulse"></div>
                </div>
              )}

              {playResult?.success === false || playResult?.message === 'already_played' ? (
                <div className="space-y-6 relative z-10">
                  <div className="text-6xl mb-4">😅</div>
                  <h2 className="text-2xl font-bold text-white">今日はもうプレイ済みです！</h2>
                  <p className="text-gray-300">
                    デイリーガチャは1日1回までです。<br/>また明日チャレンジしてね！
                  </p>
                </div>
              ) : (
                <div className="space-y-6 relative z-10">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", bounce: 0.5 }}
                    className="text-remo-accent font-black text-3xl text-glow"
                  >
                    🎉 おめでとうございます！
                  </motion.div>
                  
                  <div className="bg-black/30 rounded-xl p-6 border border-remo-main/30">
                    <div className="text-5xl mb-3">{selectedPrize?.icon || '🎁'}</div>
                    <div className="text-xl font-bold text-white mb-2">{selectedPrize?.label}</div>
                    <div className="text-4xl font-black text-remo-main mb-4">+{selectedPrize?.points} pt</div>
                    
                    <div className="h-px w-full bg-gray-600 my-4"></div>
                    
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-400 flex items-center gap-1"><FiAward /> 今月の合計</span>
                      <span className="font-bold text-lg">{playResult?.total_points || selectedPrize?.points} pt</span>
                    </div>
                    {playResult?.rank && (
                      <div className="flex justify-between items-center text-sm mt-2">
                        <span className="text-gray-400">現在のランキング</span>
                        <span className="font-bold text-remo-accent">第 {playResult.rank} 位</span>
                      </div>
                    )}
                  </div>

                  <p className="text-gray-300 font-medium pt-2">
                    また明日来てね！ 🎰
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}